import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Row, Col, Card, Spinner, Badge, Button } from 'react-bootstrap';
import { HiArrowLeft, HiOutlinePencilAlt, HiOutlineLocationMarker } from 'react-icons/hi';
import { useDispatch, useSelector } from 'react-redux';

import Wrapper from '../../components/Wrapper';
import axiosInstance from '../../utils/axios';
import { fetchUserFarms } from '../../store/features/userFarmsSlice';

const DataItem = ({ label, value }) => (
  <div className="mb-3">
    <small className="text-muted d-block fw-bold text-uppercase" style={{ fontSize: '0.75rem' }}>{label}</small>
    <span className="fs-6 text-dark">{value || '-'}</span>
  </div>
);

const PhotoGallery = ({ title, photos }) => {
  if (!photos || photos.length === 0) return null;
  
  return (
    <div className="mb-4">
      <h6 className="fw-bold text-secondary mb-3">{title}</h6>
      <div className="d-flex flex-wrap gap-3">
        {photos.map((url, idx) => (
          <a key={idx} href={url} target="_blank" rel="noopener noreferrer" className="d-block">
            <img 
              src={url} 
              alt={`${title} ${idx + 1}`} 
              className="rounded shadow-sm border border-light-subtle"
              style={{ width: '120px', height: '120px', objectFit: 'cover', transition: 'transform 0.2s' }}
              onMouseOver={e => e.currentTarget.style.transform = 'scale(1.05)'}
              onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
            />
          </a>
        ))}
      </div>
    </div>
  );
};

const ViewBooking = () => {
  const { booking_id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  const { farms } = useSelector((state) => state.userFarms || { farms: [] });

  useEffect(() => {
    const fetchBookingData = async () => {
      setLoading(true);
      try {
        const endpoint = `/super-admin/bookings/${booking_id}`;
        const bookingRes = await axiosInstance.get(endpoint);
        
        if (bookingRes.data) {
          setBooking(bookingRes.data);
          if (bookingRes.data.user_id) {
            dispatch(fetchUserFarms({ id: bookingRes.data.user_id }));
          }
        }
      } catch (error) {
        console.error("Failed to fetch booking details", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBookingData();
  }, [booking_id, dispatch]);

  if (loading) {
    return (
      <Wrapper>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <Spinner animation="border" variant="success" />
          <span className="ms-3 fw-bold text-muted">Loading Booking Details...</span>
        </div>
      </Wrapper>
    );
  }

  if (!booking) {
    return (
      <Wrapper>
        <div className="text-center py-5">
          <h3>Booking not found.</h3>
          <Button variant="primary" className="mt-3" onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <div className="py-4 px-md-3">
        {/* Header Section */}
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
          <div className="d-flex align-items-center">
            <button className="btn btn-light me-3" onClick={() => navigate(-1)}>
              <HiArrowLeft size={20} />
            </button>
            <div>
              <h2 className="fw-bolder mb-1">Booking Overview</h2>
              <p className="text-muted mb-0">ID: <span className="font-monospace text-dark">{booking.booking_id}</span></p>
            </div>
          </div>
          
          <div className="d-flex align-items-center gap-3">
            <Button 
              variant="outline-primary" 
              className="d-flex align-items-center gap-2"
              onClick={() => navigate(`/bookings/edit/${booking.booking_id}`)}
            >
              <HiOutlinePencilAlt size={18} /> Edit Booking
            </Button>
          </div>
        </div>

        <Card className="mb-4 shadow-sm">
            <Card.Body>
                <Row>

                    <Col md={3}>
                        <DataItem label="Booking ID" value={booking.booking_id}/>
                    </Col>

                    <Col md={3}>
                        <DataItem label="Farmer" value={`${booking.first_name} ${booking.last_name}`}/>
                    </Col>

                    <Col md={3}>
                        <DataItem label="Samples Count" value={booking.samples_count}/>
                    </Col>

                    <Col md={3}>
                        <DataItem label="Source" value={booking.source}/>
                    </Col>

                    <Col md={3}>
                        <DataItem label="Field Officer" value={booking.field_officer_name}/>
                    </Col>

                    <Col md={3}>
                        <DataItem
                            label="Created"
                            value={new Date(booking.created_at).toLocaleString()}
                        />
                    </Col>

                    <Col md={3}>
                        <DataItem
                            label="Updated"
                            value={new Date(booking.updated_at).toLocaleString()}
                        />
                    </Col>

                </Row>
            </Card.Body>
        </Card>

        <Row className="g-4">
          <Col md={12}>
            <h4 className="fw-bold mb-3 text-secondary border-bottom pb-2">Samples ({booking.samples?.length || 0})</h4>
            
            {booking.samples?.map((sample, index) => {
              const info = sample.sample_info || {};
              const hasPhotos = info.collection_photos?.length > 0 || info.mixing_photos?.length > 0 || info.packaging_photos?.length > 0;
              const matchingFarm = farms.find(f => String(f.farm_id) === String(sample.farm_id) || String(f.id) === String(sample.farm_id));
              const gpsPoints = matchingFarm?.geo_location?.data || [];

              return (
                <Card key={sample.sample_id} className="mb-4 shadow-sm border-0 overflow-hidden">
                  <div className="bg-success" style={{ height: '4px' }}></div>
                  <Card.Body className="p-4">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h4 className="mb-0 fw-bolder d-flex align-items-center gap-2">
                        <Badge bg="dark" className="rounded-circle p-2 fs-6">S{index + 1}</Badge>
                        {sample.sample_type} Sample
                      </h4>
                      <Badge bg="light" text="dark" className="border border-secondary px-3 py-2">
                        {sample.sample_status === 'S' ? 'Submitted' : sample.sample_status === 'I' ? 'In Progress' : 'Completed'}
                      </Badge>
                    </div>

                    <Row className="g-4">
                      {/* Data Points */}
                      <Col lg={7} xl={8}>
                        <div className="p-3 bg-light rounded h-100">
                          <h6 className="fw-bold text-success mb-3 border-bottom border-success-subtle pb-2">Sample Information</h6>
                          <Row>

                            <Col sm={4}>
                                <DataItem label="Sample ID" value={sample.sample_id}/>
                                <DataItem label="Booking ID" value={sample.booking_id}/>
                                <DataItem label="Sample No" value={sample.sample_no}/>
                                <DataItem label="Sample Type" value={sample.sample_type}/>
                            </Col>

                            <Col sm={4}>
                                <DataItem label="Farm ID" value={sample.farm_id}/>
                                <DataItem label="Farm Name" value={sample.farm_name}/>
                                <DataItem
                                    label="Farm Size"
                                    value={`${sample.farm_size} ${sample.farm_size_unit}`}
                                />
                                <DataItem label="Previous Crop" value={sample.previous_crop}/>
                                <DataItem label="Next Crop" value={sample.next_crop}/>
                            </Col>

                            <Col sm={4}>
                                <DataItem label="Sample Status" value={sample.sample_status}/>
                                <DataItem label="Tree Age (Years)" value={sample.age || '0'}/>
                                <DataItem label="Source" value={sample.source}/>
                                <DataItem label="Treatment" value={sample.treatment}/>
                                <DataItem
                                    label="Location Recommendation"
                                    value={sample.use_location_recommendation ? "Yes" : "No"}
                                />
                            </Col>

                        </Row>

                          {/* Geo Location Display */}
                          {gpsPoints.length > 0 && (
                            <div className="mt-3 p-3 bg-white border rounded">

                                <div className="d-flex align-items-center mb-2">
                                    <HiOutlineLocationMarker className="me-2 text-primary"/>
                                    <strong>
                                        GPS Coordinates ({gpsPoints.length})
                                    </strong>
                                </div>

                                {gpsPoints.map((point,index)=>(
                                    <div
                                        key={index}
                                        className="font-monospace small mb-1"
                                    >
                                        Point {index+1} :
                                        {" "}
                                        {point.latitude},
                                        {" "}
                                        {point.longitude}
                                    </div>
                                ))}

                            </div>
                        )}

                            {matchingFarm && (
                                <>
                                    <hr/>

                                    <h6 className="fw-bold text-success">
                                        Farm Details
                                    </h6>

                                    <Row>

                                        <Col md={4}>
                                            <DataItem
                                                label="Farm ID"
                                                value={matchingFarm.farm_id}
                                            />
                                        </Col>

                                        <Col md={4}>
                                            <DataItem
                                                label="Farm Name"
                                                value={matchingFarm.farm_name}
                                            />
                                        </Col>

                                        <Col md={4}>
                                            <DataItem
                                                label="Farm Size"
                                                value={`${matchingFarm.farm_size} ${matchingFarm.farm_size_unit}`}
                                            />
                                        </Col>

                                        <Col md={4}>
                                            <DataItem
                                                label="GPS Points"
                                                value={matchingFarm.geo_location?.data?.length}
                                            />
                                        </Col>

                                    </Row>
                                </>
                            )}

                          <div className="mt-3">
                            <DataItem label="Comments" value={sample.comments} />
                          </div>
                        </div>
                      </Col>

                      {/* Photos Gallery */}
                      <Col lg={5} xl={4}>
                        <div className="p-3 border rounded h-100 bg-white">
                          <h6 className="fw-bold text-success mb-3 border-bottom border-success-subtle pb-2">Verification Photos</h6>
                          
                          {hasPhotos ? (
                            <>
                              <PhotoGallery title="Collection / Digging" photos={info.collection_photos} />
                              <PhotoGallery title="Mixing" photos={info.mixing_photos} />
                              <PhotoGallery title="Packaging / Bag" photos={info.packaging_photos} />
                            </>
                          ) : (
                            <div className="text-center text-muted p-4 my-auto">
                              <p className="fst-italic mb-0">No photos uploaded for this sample.</p>
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>

                  </Card.Body>
                </Card>
              );
            })}
          </Col>
        </Row>
      </div>
    </Wrapper>
  );
};

export default ViewBooking;